import {
  GENERIC_ART_LABELS,
  GENERIC_ART_MARKUP,
  type ListingImageModel,
} from '../listing-image';
import { cn } from './utils';

/**
 * SIL-1451 - the catalogue image slot for a listing card or detail page.
 *
 * ONE slot, two fills: the resolved SKU/family photo, or generic form-factor
 * art. The slot is a fixed 4:3 box in both cases so a grid never shifts when an
 * image loads or is absent (and never collapses for a card with no image).
 *
 * `credit` placement follows the exchange handoff: on cards the licence credit
 * is a small muted overlay inside the box (so card height is unchanged), on the
 * detail page it is a caption under the image. Generic art carries no credit.
 *
 * `ListingImage` (React) and `listingImageHtml` (string, for the exchange's
 * client enhancer, which re-renders cards outside React) share their class
 * names and art markup so the two cannot drift.
 */
export type ListingImageVariant = 'card' | 'detail';

const BOX = 'relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-muted';
const PHOTO = 'absolute inset-0 h-full w-full object-contain';
const ART_WRAP = 'absolute inset-0 flex items-center justify-center text-muted-foreground';
const ART_SVG = 'h-3/5 w-3/5';
const OVERLAY_CREDIT =
  'absolute bottom-1 left-1 max-w-[calc(100%-0.5rem)] truncate rounded bg-card/85 px-1.5 py-0.5 text-2xs text-muted-foreground';
const CAPTION = 'mt-1.5 text-xs text-muted-foreground';

export function ListingImage({
  model,
  variant = 'card',
  className,
}: {
  model: ListingImageModel;
  variant?: ListingImageVariant;
  className?: string;
}) {
  const credit = model.kind === 'photo' ? model.credit : null;
  return (
    <figure className={cn('m-0', className)} data-testid="listing-image" data-image-kind={model.kind}>
      <div className={BOX}>
        {model.kind === 'photo' ? (
          <img
            src={model.url}
            alt={model.alt}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className={PHOTO}
          />
        ) : (
          <div className={ART_WRAP} data-art={model.art}>
            <svg
              viewBox="0 0 160 120"
              role="img"
              aria-label={`${model.alt} (${GENERIC_ART_LABELS[model.art]} illustration)`}
              className={ART_SVG}
              dangerouslySetInnerHTML={{ __html: GENERIC_ART_MARKUP[model.art] }}
            />
          </div>
        )}
        {credit && variant === 'card' && (
          <span className={OVERLAY_CREDIT} title={credit} data-testid="listing-image-credit">
            {credit}
          </span>
        )}
      </div>
      {credit && variant === 'detail' && (
        <figcaption className={CAPTION} data-testid="listing-image-credit">
          {credit}
        </figcaption>
      )}
    </figure>
  );
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/** String twin of `ListingImage` (see header). */
export function listingImageHtml(model: ListingImageModel, variant: ListingImageVariant = 'card'): string {
  const credit = model.kind === 'photo' ? model.credit : null;
  const fill =
    model.kind === 'photo'
      ? `<img src="${escapeHtml(model.url)}" alt="${escapeHtml(model.alt)}" loading="lazy" decoding="async" referrerpolicy="no-referrer" class="${PHOTO}">`
      : `<div class="${ART_WRAP}" data-art="${model.art}"><svg viewBox="0 0 160 120" role="img" aria-label="${escapeHtml(
          `${model.alt} (${GENERIC_ART_LABELS[model.art]} illustration)`,
        )}" class="${ART_SVG}">${GENERIC_ART_MARKUP[model.art]}</svg></div>`;
  const overlay =
    credit && variant === 'card'
      ? `<span class="${OVERLAY_CREDIT}" title="${escapeHtml(credit)}" data-testid="listing-image-credit">${escapeHtml(credit)}</span>`
      : '';
  const caption =
    credit && variant === 'detail'
      ? `<figcaption class="${CAPTION}" data-testid="listing-image-credit">${escapeHtml(credit)}</figcaption>`
      : '';
  return `<figure class="m-0" data-testid="listing-image" data-image-kind="${model.kind}"><div class="${BOX}">${fill}${overlay}</div>${caption}</figure>`;
}
